/**
 * @fileoverview The bankrupt's name versus the estate's name (MOR-2342).
 *
 * A personal-insolvency job stores two names: `jobs.bankruptName` (the
 * individual, exactly as printed on the order) and `jobs.entityName` (the
 * estate heading the practitioner chose, conventionally "Bankrupt Estate of
 * <name>"). Anything that describes the *person* — the Bankrupt job contact,
 * the "Bankrupt Name" day-one field, the insurer's appointment name — must
 * carry the person's name only. Static "Bankrupt Estate of" wording belongs
 * to the template, never to the value.
 *
 * Used by: contacts/aggregator, contacts/reconcile, contacts/queries,
 * jobs/precedents/fieldMapping.
 */

/** Matches a leading "Bankrupt Estate of" (optionally "The …"), any casing. */
const BANKRUPT_ESTATE_PREFIX_RE = /^\s*(?:the\s+)?bankrupt\s+estate\s+of\s+/i;

/**
 * True when the name is written in the estate form.
 *
 * @example
 * isBankruptEstateName("Bankrupt Estate of Alex Morgan"); // true
 * isBankruptEstateName("Alex Morgan"); // false
 */
export function isBankruptEstateName(name: string | null | undefined): boolean {
  return !!name && BANKRUPT_ESTATE_PREFIX_RE.test(name);
}

/**
 * The person's name with any "Bankrupt Estate of" prefix removed.
 *
 * @example
 * stripBankruptEstatePrefix("The Bankrupt Estate of Alex Morgan"); // "Alex Morgan"
 */
export function stripBankruptEstatePrefix(name: string): string {
  return name.replace(BANKRUPT_ESTATE_PREFIX_RE, "").trim();
}

/**
 * The individual's name for a personal-insolvency job: the explicit
 * `bankruptName` when recorded, otherwise the estate name with its prefix
 * removed. Undefined when the job has neither.
 *
 * @example
 * resolveBankruptPersonName({ bankruptName: undefined, entityName: "Bankrupt Estate of Alex" }); // "Alex"
 */
export function resolveBankruptPersonName(job: {
  bankruptName?: string | null;
  entityName?: string | null;
}): string | undefined {
  const explicit = job.bankruptName?.trim();
  if (explicit) return explicit;
  const fromEstate = job.entityName
    ? stripBankruptEstatePrefix(job.entityName)
    : "";
  return fromEstate || undefined;
}
